import CombinedMentionList from '@/components/common/CombinedMentionList.vue'
import { closeAllMentionsTooltips } from '@/components/mention-helper'
import { GroupState } from '@/composition/groups'
import { isTabVisibleByName } from '@/helper/groups.helper'
import { TabName } from '@/models/TabConfiguration'
import { CoursesService } from '@/service/CoursesService'
import { EventsService } from '@/service/EventsService'
import { Node, mergeAttributes } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { PluginKey } from '@tiptap/pm/state'
import { VueRenderer } from '@tiptap/vue-3'
import tippy from 'tippy.js'
import { Suggestion, SuggestionOptions } from './suggestions'

export type OtherMentionOptions = {
  HTMLAttributes: Record<string, any>
  renderLabel: (props: {
    options: OtherMentionOptions
    node: ProseMirrorNode
  }) => string
  suggestion: Omit<SuggestionOptions, 'editor'>
}

export const OtherMentionPluginKey = new PluginKey('other-mention')

const getChannelSuggestions = (query: string) => {
  const channels = GroupState.state.channelList || []
  const filteredChannels = channels
    .filter((channel: any) =>
      channel.name.toLowerCase().includes(query.toLowerCase())
    )
    .slice(0, 5) // Limit channels to 5
    .map((channel: any) => ({
      id: channel._id,
      label: channel.name,
      type: 'channel-mention' as const,
      slug: channel.slug,
      isPrivate: channel.visibility === 'PRIVATE',
      icon: channel.icon,
    }))

  return filteredChannels
}

const getEventsSuggestions = async (query: string) => {
  // Check if Events tab is visible in group settings using backend enum
  if (!isTabVisibleByName(TabName.EVENTS)) {
    return []
  }

  // As we are showing only 5 events once, so Show date range for 12 months ahead and 2 months back to make it easier for user to find events
  const now = new Date()
  const startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1)
  const endDate = new Date(now.getFullYear(), now.getMonth() + 12, 0)

  const finalQuery = {
    search: query,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  }

  try {
    const events = await EventsService.fetchTaggableEvents(finalQuery)
    const filteredEvents = events.map(event => {
      // Save this in milliseconds to reduce stored string size in db
      const eventDate = new Date(
        event.isRecurring ? event.nextOccurrence : event.startTime
      ).getTime()
      return {
        id: event._id,
        label: event.title,
        type: 'events-mention' as const,
        timestamp: eventDate,
      }
    })
    return filteredEvents
  } catch (_error) {
    console.error('Error fetching events', _error)
    return []
  }
}

const getCoursesSuggestions = async (query: string) => {
  // Check if Learning tab is visible in group settings using backend enum
  if (!isTabVisibleByName(TabName.LEARNING)) {
    return []
  }

  try {
    // Ensure courses are loaded
    const courses = await CoursesService.getCoursesSuggestions(query)
    const filteredCourses = courses.map((course: any) => ({
      id: course.productId,
      label: course.title,
      type: 'courses-mention' as const,
      description: course.description,
    }))
    return filteredCourses
  } catch (_error) {
    console.error('Error fetching courses', _error)
    return []
  }
}

const getMentionSuggestions = async (query: string) => {
  const channels = await getChannelSuggestions(query)
  const [events, courses] = await Promise.all([
    getEventsSuggestions(query),
    getCoursesSuggestions(query),
  ])
  return [...courses, ...events, ...channels]
}

// Store component reference outside the render function
let currentComponent: any = null

// Store popup reference outside the render function
let currentPopup: any = null

export const OtherMentions = Node.create<OtherMentionOptions>({
  name: 'other-mention',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel({ node }) {
        return `#${node.attrs.label ?? node.attrs.id}`
      },
      suggestion: {
        char: '#',
        allowSpaces: true,
        pluginKey: OtherMentionPluginKey,
        async items({ query, editor }: { query: string; editor: any }) {
          getMentionSuggestions(query).then(data => {
            if (currentComponent) {
              currentComponent?.updateProps({
                items: data,
                isLoading: false,
              })
              // Force show tooltip if data is available and popup exists but is hidden
              if (data.length > 0 && currentPopup && currentPopup[0]) {
                const suggestionState = OtherMentionPluginKey.getState(
                  editor.state
                )

                // Check multiple conditions before showing popup
                if (
                  suggestionState &&
                  suggestionState.active &&
                  !currentPopup[0].state.isVisible &&
                  editor.isFocused && // Editor must be focused
                  suggestionState.query === query && // Query must still match
                  suggestionState.range // Range must still exist
                ) {
                  // Additional check: ensure cursor is still in the mention range
                  const { from, to } = editor.state.selection
                  const mentionRange = suggestionState.range

                  if (from >= mentionRange.from && to <= mentionRange.to) {
                    currentPopup[0].show()
                  }
                }
              }
            }
          })
          // This a placeholder for the loading state, doesn't directly affect the component
          const currentSuggestions = currentComponent?.props.items ?? []
          if (currentSuggestions.length > 0) {
            return currentSuggestions
          }
          // don't show loading intermediate loading state if user has typed something
          return query?.length > 0
            ? []
            : [
                {
                  id: 'loading',
                  label: 'Loading...',
                  type: 'loading',
                },
              ]
        },
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()

          window.getSelection()?.collapseToEnd()
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!$from.parent.type.contentMatch.matchType(type)
          return allow
        },
        render: () => {
          let component
          let popup

          return {
            onStart: props => {
              component = new VueRenderer(CombinedMentionList, {
                props,
                editor: props.editor,
              })

              // Store reference so items() can access it
              currentComponent = component

              if (!props.clientRect) {
                return
              }

              popup = tippy('body', {
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                getReferenceClientRect: props.clientRect as any,
                popperOptions: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport',
                      },
                    },
                  ],
                },
                hideOnClick: true,
                onShow(instance) {
                  // Add mobile-specific event listeners to close on outside touch
                  const handleOutsideTouch = (event: Event) => {
                    const target = event.target as Element
                    if (!instance.popper.contains(target)) {
                      instance.hide()
                    }
                  }

                  // Add touchstart listener for mobile devices
                  document.addEventListener('click', handleOutsideTouch, {
                    passive: true,
                  })

                  // Store cleanup function
                  ;(instance as any)._outsideTouchHandler = handleOutsideTouch
                },
                onHide(instance) {
                  // Clean up mobile event listeners
                  if ((instance as any)._outsideTouchHandler) {
                    document.removeEventListener(
                      'click',
                      (instance as any)._outsideTouchHandler
                    )
                    delete (instance as any)._outsideTouchHandler
                  }
                },
              })

              // Store popup reference globally
              currentPopup = popup
            },

            async onUpdate(props) {
              component?.updateProps(props)

              if (!props.clientRect) {
                return
              }

              if (popup.length > 0 && !popup[0].state.isDestroyed) {
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                })
              }
            },

            onKeyDown(props) {
              if (!popup || !popup?.length || popup[0]?.state?.isDestroyed) {
                return false
              }

              if (props.event.key === 'Escape') {
                popup[0].hide()
                return true
              }
              return component?.ref?.onKeyDown(props)
            },

            onExit() {
              currentComponent = null
              currentPopup = null
              popup[0].destroy()
              component.destroy()
            },
          }
        },
      },
    }
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: attributes =>
          attributes.id ? { 'data-id': attributes.id } : {},
      },
      type: {
        default: null,
        parseHTML: element => element.getAttribute('data-type'),
        renderHTML: attributes =>
          attributes.type ? { 'data-type': attributes.type } : {},
      },
      icon: {
        default: null,
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => {
          return attributes.icon ? { 'data-icon': attributes.icon } : {}
        },
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label'),
        renderHTML: attributes =>
          attributes.label ? { 'data-label': attributes.label } : {},
      },
      timestamp: {
        default: null,
        parseHTML: element => element.getAttribute('data-timestamp'),
        renderHTML: attributes =>
          attributes.timestamp
            ? { 'data-timestamp': attributes.timestamp }
            : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
      {
        tag: `a[data-type="${this.name}"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
      {
        tag: `a[data-type="channel-mention"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
      {
        tag: `a[data-type="events-mention"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
      {
        tag: `a[data-type="courses-mention"]`,
        priority: 10001, // higher than link extension's 1000, so on paste it will be treated as mention not link
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(
        {
          'data-type': node.attrs.type || this.name,
          'data-id': node.attrs.id,
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ]
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    })
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }
          closeAllMentionsTooltips()

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              )

              return false
            }
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
